import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserRole, getUserId } from '../helpers/local_storage_helpers';

const Sheets = () => {
  const navigate = useNavigate();
  const [mySheets, setMySheets] = useState([
    { id: 'custom', name: 'My Custom Lead Sheet', lastModified: '2024-01-15', rows: 25 },
    { id: 'leads-q1', name: 'Q1 Leads', lastModified: '2024-01-14', rows: 45 },
    { id: 'follow-ups', name: 'Follow-ups', lastModified: '2024-01-13', rows: 12 },
  ]);
  const [assignedSheets, setAssignedSheets] = useState([
    { id: 'shared-1', name: 'Shared Lead Pool', lastModified: '2024-01-15', rows: 67, assignedBy: 'Admin' },
    { id: 'team-1', name: 'Team Leads', lastModified: '2024-01-14', rows: 23, assignedBy: 'Manager' },
  ]);
  const [isCreating, setIsCreating] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');

  const handleCreateSheet = () => {
    if (newSheetName.trim()) {
      const newSheet = {
        id: `sheet-${Date.now()}`,
        name: newSheetName,
        lastModified: new Date().toISOString().split('T')[0],
        rows: 0
      };
      setMySheets([newSheet, ...mySheets]);
      setNewSheetName('');
      setIsCreating(false);
      navigate(`/sheets/${newSheet.id}`);
    }
  };

  const handleDeleteSheet = (sheetId) => {
    if (window.confirm('Are you sure you want to delete this sheet?')) {
      setMySheets(mySheets.filter(s => s.id !== sheetId));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Sheets</h1>
          <p className="text-sm text-gray-600">Manage your lead spreadsheets</p>
        </div>
        <div className="flex gap-2">
          {isCreating ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newSheetName}
                onChange={(e) => setNewSheetName(e.target.value)}
                placeholder="Sheet name"
                className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateSheet()}
                autoFocus
              />
              <button
                onClick={handleCreateSheet}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setNewSheetName('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <span>+</span>
              New Sheet
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My Sheets */}
        <div className="bg-white shadow rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b">
            <h2 className="text-lg font-semibold text-gray-800">My Sheets</h2>
            <p className="text-sm text-gray-600">{mySheets.length} sheets</p>
          </div>
          <div className="divide-y">
            {mySheets.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No sheets yet. Create your first sheet!
              </div>
            ) : (
              mySheets.map((sheet) => (
                <div key={sheet.id} className="p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800">{sheet.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span>Modified: {formatDate(sheet.lastModified)}</span>
                        <span>{sheet.rows} rows</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/sheets/${sheet.id}`)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Open
                      </button>
                      <button
                        onClick={() => handleDeleteSheet(sheet.id)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Assigned Sheets */}
        <div className="bg-white shadow rounded overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b">
            <h2 className="text-lg font-semibold text-gray-800">Assigned to me</h2>
            <p className="text-sm text-gray-600">{assignedSheets.length} sheets</p>
          </div>
          <div className="divide-y">
            {assignedSheets.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No assigned sheets yet.
              </div>
            ) : (
              assignedSheets.map((sheet) => (
                <div key={sheet.id} className="p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800">{sheet.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span>Modified: {formatDate(sheet.lastModified)}</span>
                        <span>{sheet.rows} rows</span>
                        <span>By: {sheet.assignedBy}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/sheets/${sheet.id}`)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Open
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 bg-blue-50 rounded p-4">
        <h3 className="text-lg font-semibold text-blue-800 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => navigate('/sheets/custom')}
            className="p-3 bg-white rounded border border-blue-200 hover:border-blue-300 transition-colors text-left"
          >
            <div className="text-blue-600 font-medium text-sm">Open Custom Sheet</div>
            <div className="text-xs text-gray-600">Start working on your main lead sheet</div>
          </button>
          <button
            onClick={() => setIsCreating(true)}
            className="p-3 bg-white rounded border border-blue-200 hover:border-blue-300 transition-colors text-left"
          >
            <div className="text-blue-600 font-medium text-sm">Create New Sheet</div>
            <div className="text-xs text-gray-600">Start fresh with a new spreadsheet</div>
          </button>
          <button
            className="p-3 bg-white rounded border border-blue-200 hover:border-blue-300 transition-colors text-left"
          >
            <div className="text-blue-600 font-medium text-sm">Import Data</div>
            <div className="text-xs text-gray-600">Upload CSV or Excel file</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sheets;


